import { useCallback, useEffect, useRef } from "react";
import { usePostApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { updateUserContactNumber as updateEntriesUser } from "src/redux/slices/entriesGeneratorSlice";
import {
  setFeature,
  updateUserContactNumber as updateGeneratorUser,
} from "src/redux/slices/generatorSlice";

/**
 * Custom hook to handle saving contact numbers when a user is selected
 * and the contact number field is modified.
 */
export const useSaveContactNumber = () => {
  const { post } = usePostApi();
  const dispatch = useDispatch();
  const { features } = useSelector(state => state.generatorSlice);
  const featuresRef = useRef(features);

  // Keep the ref updated with latest features
  useEffect(() => {
    featuresRef.current = features;
  }, [features]);

  /**
   * Saves contact number if it exists in features and a user ID is available
   */
  const saveContactNumber = useCallback(async () => {
    // Use ref to get latest features to avoid stale closure
    const latestFeatures = featuresRef.current;

    const contactNumber = latestFeatures["contactNumber"];
    const selectedUserId = latestFeatures["contactNumberUserId"];

    // Early return if no contact number or user ID
    if (!contactNumber || !selectedUserId) {
      return;
    }

    try {
      await post({
        request: "update-contect-num",
        body: {
          params: {
            userid: selectedUserId,
            contactNum: contactNumber,
          },
        },
      });

      // Update the user's contact number in local state to reflect the change in dropdown
      // Update both Redux slices to ensure consistency
      dispatch(
        updateGeneratorUser({
          userid: selectedUserId,
          contactNumber: contactNumber,
        }),
      );
      dispatch(
        updateEntriesUser({
          userid: selectedUserId,
          contactNumber: contactNumber,
        }),
      );
    } catch (error) {
      console.error("Failed to save contact number:", error);
    }
  }, [post, dispatch]);

  /**
   * Clears the contact number data from Redux features
   */
  const clearContactNumber = useCallback(() => {
    dispatch(setFeature({ key: "contactNumber", feature: "" }));
    dispatch(setFeature({ key: "contactNumberUserId", feature: "" }));
  }, [dispatch]);

  return {
    saveContactNumber,
    clearContactNumber,
    hasContactNumber: !!(
      features["contactNumber"] && features["contactNumberUserId"]
    ),
  };
};
